import { abrePDF } from "../../utils/pdf";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { fDMA } from "../../utils/fecha";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import moment from "moment";

/**
 * Ventas ya surtidas, para ALMACÉN.
 *
 * Es una vista de consulta: sirve para confirmar qué se envió en cada nota
 * (por si se mandó algo distinto) y para nada más. NO muestra precios,
 * importes, saldos ni estado fiscal, y no tiene ninguna acción que modifique.
 *
 * Se separó de ListadoVentas en vez de esconderle columnas porque esa vista
 * es de administración: factura, cancela, cobra y edita notas. Darle acceso
 * con columnas ocultas dejaría los endpoints al alcance de un perfil que no
 * debe tocarlos.
 *
 * Permiso: ventasAlmacen.
 */
function ListadoVentasAlmacen() {
  const { user } = useContext(AuthContext);
  const URL_SURTIDOS = process.env.REACT_APP_URL_SURTIDOS;
  const URL_ARTICULOS_SURTIDOS = process.env.REACT_APP_URL_ARTICULOS_SURTIDOS;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  const endDate = moment(hoy).format("YYYY-MM-DD");
  const startDate = moment(inicio).format("YYYY-MM-DD");

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [clientes, setClientes] = useState([]);

  const [comments, setComments] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  // Detalle de lo que se envió en una nota
  const [modalDetalle, setModalDetalle] = useState(false);
  const [detalleRows, setDetalleRows] = useState([]);
  const [detalleNota, setDetalleNota] = useState("");
  const [detalleLoading, setDetalleLoading] = useState(false);

  const ITEMS_PER_PAGE = 50;

  const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
  const dec = (n, d = 2) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits: d }).format(
      Number(n) || 0
    );

  const headers = [
    { name: "Nota", field: "numero", sortable: true },
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "Cotizacion", field: "pedido", sortable: true },
    { name: "Cliente", field: "clientes", sortable: true },
    { name: "Piezas", field: "total_cantidad", sortable: true },
    { name: "Paqueteria", field: "paqueteria", sortable: true },
    { name: "No. Guia", field: "noGuia", sortable: true },
    { name: "Detalle", field: "detalle", sortable: false },
  ];

  useMemo(() => {
    axios
      .get(`${URL_SURTIDOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        const arr = (response.data || [])
          .filter((a) => a.is_active !== "No")
          .sort((a, b) => (a.idSurtido < b.idSurtido ? 1 : -1))
          .map((a) => {
            const ped = a.pedidos?.[0] || {};
            const cli = a.clientes?.[0] || ped.clientes?.[0] || {};
            const pedCli = ped.clientes?.[0] || {};
            return {
              _id: a._id,
              numero: a.idSurtido,
              pedido: ped.idPedido || "",
              fecha: a.fecha,
              clientes: pedCli.nombre_comercial || cli.nombre_comercial || "NA",
              idClientes: pedCli._id || cli._id || "",
              total_cantidad: Number(a.total_cantidad) || 0,
              paqueteria: cli.paqueteria || "NA",
              noGuia: a.noGuia || "",
            };
          });
        setComments(arr);
      })
      .catch((err) => console.log(err));
  }, [selectedFechaInicio, selectedFechaFin]);

  useEffect(() => {
    axios
      .get(`${URL_CLIENTES}GeneralActivos`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => setClientes(response.data || []))
      .catch((err) => console.log(err));
  }, []);

  async function abrirDetalle(id, numero) {
    setDetalleNota(numero);
    setDetalleRows([]);
    setDetalleLoading(true);
    setModalDetalle(true);
    try {
      const res = await axios.get(`${URL_ARTICULOS_SURTIDOS}/surtido/${id}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      });
      // Se agrupa por artículo: una nota puede traer el mismo artículo en
      // varios renglones y a almacén le sirve el total por artículo.
      const grupos = {};
      for (const a of res.data || []) {
        const art = a?.articulos?.[0];
        if (!art) continue;
        const id = String(art._id);
        const cantidad = Number(a.cantidad || 0);
        const pxc = Number(art.piezasPorCaja || 1) || 1;
        grupos[id] = grupos[id] || {
          codigo: art.codigo || "",
          nombre: art.nombre || "",
          cantidad: 0,
          cajas: 0,
          piezasPorCaja: pxc,
        };
        grupos[id].cantidad += cantidad;
        grupos[id].cajas += cantidad / pxc;
      }
      setDetalleRows(
        Object.values(grupos).sort((a, b) =>
          (a.codigo || "").localeCompare(b.codigo || "")
        )
      );
    } catch (err) {
      console.log(err);
    } finally {
      setDetalleLoading(false);
    }
  }

  const commentsData = useMemo(() => {
    let c = comments;
    if (search) {
      const s = search.toLowerCase();
      c = c.filter(
        (x) =>
          String(x.clientes).toLowerCase().includes(s) ||
          String(x.numero).includes(search) ||
          String(x.noGuia).toLowerCase().includes(s)
      );
    }
    if (selectedCliente) {
      c = c.filter((x) => x.idClientes === selectedCliente);
    }
    setTotalItems(c.length);

    const numericos = ["numero", "pedido", "total_cantidad"];
    if (sorting.field) {
      const dir = sorting.order === "asc" ? 1 : -1;
      c = [...c].sort((a, b) =>
        numericos.includes(sorting.field)
          ? dir *
            ((parseFloat(a[sorting.field]) || 0) -
              (parseFloat(b[sorting.field]) || 0))
          : dir *
            String(a[sorting.field] ?? "").localeCompare(
              String(b[sorting.field] ?? "")
            )
      );
    }

    setDatosFiltrados(c);
    return c.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting, selectedCliente]);

  const totalPiezas = (datosFiltrados || []).reduce(
    (t, a) => t + (Number(a.total_cantidad) || 0),
    0
  );

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    doc.setFontSize(12);
    doc.text("Ventas surtidas", 14, 18);
    doc.setFontSize(9);
    doc.text(
      `${selectedFechaInicio} - ${selectedFechaFin}   ·   Notas: ${datosFiltrados.length}   ·   Piezas: ${num(totalPiezas)}`,
      14,
      25
    );
    doc.autoTable({
      head: [["Nota", "Fecha", "Cotizacion", "Cliente", "Piezas", "Paqueteria", "No. Guia"]],
      body: datosFiltrados.map((a) => [
        a.numero,
        a.fecha,
        a.pedido,
        a.clientes,
        num(a.total_cantidad),
        a.paqueteria,
        a.noGuia,
      ]),
      foot: [["", "", "", "TOTAL", num(totalPiezas), "", ""]],
      headStyles: { fillColor: ["#911c04"], fontSize: 8, halign: "center" },
      footStyles: { fillColor: ["#911c04"], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      theme: "grid",
      startY: 30,
      margin: { left: 8, right: 8 },
    });
    abrePDF(doc, "VentasSurtidas.pdf");
  }

  function excel() {
    const data = datosFiltrados.map((a) => ({
      Nota: a.numero,
      Fecha: a.fecha,
      Cotizacion: a.pedido,
      Cliente: a.clientes,
      Piezas: Number(a.total_cantidad) || 0,
      Paqueteria: a.paqueteria,
      NoGuia: a.noGuia,
    }));
    data.push({
      Nota: "",
      Fecha: "",
      Cotizacion: "",
      Cliente: "TOTAL",
      Piezas: totalPiezas,
      Paqueteria: "",
      NoGuia: "",
    });
    const columnas = ["Nota", "Fecha", "Cotizacion", "Cliente", "Piezas", "Paqueteria", "NoGuia"];
    new ExportJsonExcel({
      fileName: `VentasSurtidas-${selectedFechaInicio}-${selectedFechaFin}`,
      datas: [
        {
          sheetData: data,
          sheetName: "VentasSurtidas",
          sheetFilter: columnas,
          sheetHeader: ["Nota", "Fecha", "Cotizacion", "Cliente", "Piezas", "Paqueteria", "No. Guia"],
        },
      ],
    }).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.ventasAlmacen || user.menu_surtidos ? (
        <div className="card container col-12">
          <Row>
            <Col sm={8}>
              <Button size="sm" href="/MenuAlmacen" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Ventas Surtidas</h3>

          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Cliente</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedCliente}
                onChange={(e) => {
                  setSelectedCliente(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {clientes
                  .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                  .map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.nombre_comercial}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Total piezas</Label>
              <Input bsSize="sm" value={num(totalPiezas)} disabled />
            </Col>
          </Row>
          <br />

          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tbody>
              {commentsData.map((a) => (
                <tr key={a._id}>
                  <td>{a.numero}</td>
                  <td>{fDMA(a.fecha)}</td>
                  <td>{a.pedido}</td>
                  <td>{a.clientes}</td>
                  <td align="right">{num(a.total_cantidad)}</td>
                  <td>{a.paqueteria}</td>
                  <td>{a.noGuia}</td>
                  <td>
                    <Button
                      size="sm"
                      color="info"
                      title="Ver qué se envió"
                      onClick={() => abrirDetalle(a._id, a.numero)}
                    >
                      <i className="fas fa-boxes"></i>
                    </Button>
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                <td colSpan={4} align="right">
                  TOTAL ({datosFiltrados.length} notas)
                </td>
                <td align="right">{num(totalPiezas)}</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </Table>

          <Modal size="lg" isOpen={modalDetalle} toggle={() => setModalDetalle(!modalDetalle)}>
            <ModalHeader toggle={() => setModalDetalle(!modalDetalle)}>
              <h4>Nota {detalleNota} — artículos enviados</h4>
            </ModalHeader>
            <ModalBody>
              {detalleLoading ? (
                <div align="center">
                  <Spinner color="primary" />
                </div>
              ) : (
                <Table size="sm" striped borderless>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descripción</th>
                      <th align="right">Piezas</th>
                      <th align="right">Cajas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalleRows.map((r) => (
                      <tr key={r.codigo}>
                        <td>{r.codigo}</td>
                        <td>{r.nombre}</td>
                        <td align="right">{num(r.cantidad)}</td>
                        <td align="right">{dec(r.cajas)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                      <td colSpan={2} align="right">
                        TOTAL
                      </td>
                      <td align="right">
                        {num(detalleRows.reduce((t, r) => t + r.cantidad, 0))}
                      </td>
                      <td align="right">
                        {dec(detalleRows.reduce((t, r) => t + r.cajas, 0))}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </ModalBody>
          </Modal>

          <br />
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoVentasAlmacen;
